import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '@/store/authSlice';
import { selectCurrentGame, fetchGameState, updateGameState } from '@/store/gameSlice';
import { apiClient } from '@/services/api';
import type { AppDispatch } from '@/store';
import type { Resources } from '@/types';
import AuctionArena from '@/components/Auction/AuctionArena';
import { sellerOpeningPrice } from './sellerOpening';
import { ensureMusic, playCountdown, playSound, stopMusic } from '@/audio/rushAudio';
import { MuteButton } from '@/components/MuteButton/MuteButton';
import { QuitGameButton } from '@/components/QuitGame/QuitGameButton';
import { StoreLandOffer } from '@/components/QuitGame/StoreLandOffer';
import AuctionPlayerPanel from '@/components/Auction/AuctionPlayerPanel';
import AuctionTradeHistory from '@/components/Auction/AuctionTradeHistory';
import './Auction.css';

// Resource types available for trading
export type AuctionResourceType = 'energy' | 'minerals' | 'food' | 'oxygen' | 'water' | 'helium3';

// Auction phase types
export type AuctionPhase = 'role-selection' | 'trading' | 'completed';

// Resource configuration
export const AUCTION_RESOURCES: Array<{
  id: AuctionResourceType;
  name: string;
  icon: string;
  emoji: string;
  spoilage: number;
  basePrice: number;
  minPrice: number;
  maxPrice: number;
}> = [
  { id: 'energy', name: 'Energy', icon: 'energy-8bit.png', emoji: '⚡', spoilage: 0.25, basePrice: 35, minPrice: 15, maxPrice: 70 },
  { id: 'minerals', name: 'Minerals', icon: 'minerals-8bit.png', emoji: '⛏️', spoilage: 0, basePrice: 50, minPrice: 20, maxPrice: 100 },
  { id: 'food', name: 'Food', icon: 'food-8bit.png', emoji: '🌱', spoilage: 0.50, basePrice: 30, minPrice: 10, maxPrice: 60 },
  { id: 'oxygen', name: 'Oxygen', icon: 'oxygen-8bit.png', emoji: '💨', spoilage: 0, basePrice: 25, minPrice: 10, maxPrice: 50 },
  { id: 'water', name: 'Water', icon: 'water-8bit.png', emoji: '💧', spoilage: 0, basePrice: 20, minPrice: 5, maxPrice: 40 },
  { id: 'helium3', name: 'Helium-3', icon: 'helium3-8bit.png', emoji: '⚛️', spoilage: 0, basePrice: 75, minPrice: 30, maxPrice: 150 },
];

// Timing constants
const ROLE_SELECTION_TIME = 10; // 10 seconds to choose buyer/seller
const TRADING_TIME = 30; // 30 seconds to buy and sell this item

// Player position in the auction
export interface AuctionPlayer {
  playerId: string;
  username: string;
  avatar: string;
  playerNumber: number;
  color: string;
  role: 'buyer' | 'seller' | 'undecided';
  currentPrice: number;
  targetPrice: number;
  isWalking: boolean;
  quantity: number;
  isAI: boolean;
  resources: Resources;
  money: number;
  producing?: AuctionResourceType[];
}

// Trade record
export interface AuctionTrade {
  id: string;
  resource: AuctionResourceType;
  buyerId: string;
  buyerName: string;
  sellerId: string;
  sellerName: string;
  price: number;
  quantity: number;
  timestamp: string;
  isStoreTransaction: boolean;
}

// Auction state
export interface AuctionState {
  currentResource: AuctionResourceType;
  currentResourceIndex: number;
  timeRemaining: number;
  phase: AuctionPhase;
  storeInventory: Record<AuctionResourceType, number>;
  storePrices: {
    buy: Record<AuctionResourceType, number>;
    sell: Record<AuctionResourceType, number>;
  };
  trades: AuctionTrade[];
  players: AuctionPlayer[];
}

// Default store inventory
const getDefaultStoreInventory = (): Record<AuctionResourceType, number> => ({
  energy: 20,
  minerals: 15,
  food: 25,
  oxygen: 30,
  water: 20,
  helium3: 5,
});

const MULE_INPUTS = new Set<AuctionResourceType>(['minerals', 'energy']);

/**
 * Quoted once, before the item's trading window.
 * A short warehouse pays more, especially for mule inputs. A glut pays less and sells for less.
 */
export function quoteStore(resourceId: AuctionResourceType, stock: number) {
  const config = AUCTION_RESOURCES.find((resource) => resource.id === resourceId);
  const base = config?.basePrice ?? 20;
  const maxPrice = config?.maxPrice ?? base * 2;
  const minPrice = config?.minPrice ?? Math.round(base * 0.4);
  const full = getDefaultStoreInventory()[resourceId] || 1;
  const ratio = stock / full;
  const glut = Math.max(0, ratio - 1);
  const scarcity = Math.max(0, 1 - ratio);
  const normalBuy = Math.round(base * 0.7);
  const normalSell = Math.round(base * 1.3);
  const eagerBuy = Math.round(base * (MULE_INPUTS.has(resourceId) ? 1.15 : 1));
  const sellRaw = normalSell - glut * (normalSell - normalBuy) * 0.65 + scarcity * (maxPrice - normalSell) * 0.5;
  const sell = Math.round(Math.max(normalBuy + 2, Math.min(maxPrice, sellRaw)));
  const buyRaw = normalBuy + scarcity * (eagerBuy - normalBuy) - glut * (normalBuy - minPrice) * 0.8;
  const buy = Math.round(Math.max(minPrice, Math.min(sell - 1, buyRaw)));
  return { buy, sell };
}

function chartClamp(price: number, resourceId: AuctionResourceType) {
  const config = AUCTION_RESOURCES.find((resource) => resource.id === resourceId);
  const minPrice = config?.minPrice ?? price;
  const maxPrice = config?.maxPrice ?? price;
  return Math.round(Math.max(minPrice, Math.min(maxPrice, price)));
}

function openSeller(sell: number, resourceId: AuctionResourceType) {
  const config = AUCTION_RESOURCES.find((resource) => resource.id === resourceId);
  return sellerOpeningPrice(sell, config?.maxPrice ?? sell);
}

function lockedQuote(prices: AuctionState['storePrices'], resourceId: AuctionResourceType) {
  return {
    buy: prices.buy[resourceId],
    sell: prices.sell[resourceId],
  };
}

/** A buyer stops on the store sell line. A seller stops on the store buy line. */
function bandLimit(
  role: AuctionPlayer['role'],
  price: number,
  quote: { buy: number; sell: number },
  resourceId: AuctionResourceType,
) {
  const charted = chartClamp(price, resourceId);
  if (role === 'buyer') return Math.min(charted, quote.sell);
  if (role === 'seller') return Math.max(charted, quote.buy);
  return charted;
}

function nothingLeftToSell(state: AuctionState) {
  const id = state.currentResource;
  const sellerHas = state.players.some(
    (player) => player.role === 'seller' && (player.resources?.[id] || 0) > 0,
  );
  const storeHas = (state.storeInventory[id] || 0) > 0;
  return !sellerHas && !storeHas;
}

function advancePastTrading(prev: AuctionState): AuctionState {
  const nextIndex = prev.currentResourceIndex + 1;
  if (nextIndex >= AUCTION_RESOURCES.length) {
    return { ...prev, phase: 'completed', timeRemaining: 0 };
  }
  const nextResource = AUCTION_RESOURCES[nextIndex];
  const middlePrice = (nextResource.maxPrice + nextResource.minPrice) / 2;
  const quote = quoteStore(nextResource.id, prev.storeInventory[nextResource.id] || 0);
  return {
    ...prev,
    currentResource: nextResource.id,
    currentResourceIndex: nextIndex,
    phase: 'role-selection',
    timeRemaining: ROLE_SELECTION_TIME,
    players: prev.players.map((player) => ({
      ...player,
      role: 'undecided' as const,
      currentPrice: middlePrice,
      targetPrice: middlePrice,
      isWalking: false,
    })),
    storePrices: {
      buy: { ...prev.storePrices.buy, [nextResource.id]: quote.buy },
      sell: { ...prev.storePrices.sell, [nextResource.id]: quote.sell },
    },
  };
}

/** Buy or sell from what this player holds, and what their plots produce. */
function chooseRole(
  player: AuctionPlayer,
  resourceId: AuctionResourceType,
  quote: { buy: number; sell: number },
  storeStock: number,
): { role: 'buyer' | 'seller'; price: number } {
  const held = player.resources?.[resourceId] || 0;
  const produces = (player.producing || []).includes(resourceId);
  const cornerUnits = Math.min(storeStock, 4);
  const corners = produces
    && storeStock > 0
    && player.playerNumber % 2 === 0
    && player.money >= quote.sell * Math.max(1, cornerUnits);
  if (corners) return { role: 'buyer', price: quote.sell };
  if (held >= 8) return { role: 'seller', price: openSeller(quote.sell, resourceId) };
  if (held <= 2) return { role: 'buyer', price: quote.buy };
  if (produces && player.money >= quote.sell) return { role: 'buyer', price: quote.buy };
  return held >= 5
    ? { role: 'seller', price: openSeller(quote.sell, resourceId) }
    : { role: 'buyer', price: quote.buy };
}

function pricesFromInventory(inventory: Record<AuctionResourceType, number>) {
  const buy: Record<string, number> = {};
  const sell: Record<string, number> = {};
  AUCTION_RESOURCES.forEach((resource) => {
    const quote = quoteStore(resource.id, inventory[resource.id] || 0);
    buy[resource.id] = quote.buy;
    sell[resource.id] = quote.sell;
  });
  return { buy, sell } as AuctionState['storePrices'];
}

function producingFor(plots: Array<{ claimedBy?: { playerId?: string }; ownerId?: string; rover?: { type?: string; playerId?: string } }> | undefined, playerId: string): AuctionResourceType[] {
  const found = new Set<AuctionResourceType>();
  for (const plot of plots || []) {
    const owner = plot.claimedBy?.playerId || plot.ownerId || plot.rover?.playerId;
    if (owner !== playerId) continue;
    const type = plot.rover?.type;
    if (type === 'mining') found.add('minerals');
    else if (type === 'energy' || type === 'food' || type === 'water' || type === 'helium3') found.add(type);
  }
  return [...found];
}

function fromSharedAuction(live: NonNullable<NonNullable<import('@/types').GameState['auction']>['live']>): AuctionState {
  return {
    currentResource: live.currentResource,
    currentResourceIndex: live.currentResourceIndex,
    timeRemaining: live.timeRemaining,
    phase: live.phase,
    storeInventory: {
      energy: live.storeInventory.energy || 0,
      minerals: live.storeInventory.minerals || 0,
      food: live.storeInventory.food || 0,
      oxygen: live.storeInventory.oxygen || 0,
      water: live.storeInventory.water || 0,
      helium3: live.storeInventory.helium3 || 0,
    },
    storePrices: {
      buy: live.storePrices.buy as AuctionState['storePrices']['buy'],
      sell: live.storePrices.sell as AuctionState['storePrices']['sell'],
    },
    trades: (live.trades || []).map((trade, index) => {
      const row = trade as Record<string, string | number>;
      return {
        id: `${row.buyerId}-${row.sellerId}-${index}`,
        resource: row.resource as AuctionResourceType,
        buyerId: String(row.buyerId || ''),
        buyerName: String(row.buyerName || ''),
        sellerId: String(row.sellerId || ''),
        sellerName: String(row.sellerName || ''),
        price: Number(row.price) || 0,
        quantity: Number(row.quantity) || 0,
        timestamp: '',
        isStoreTransaction: row.buyerId === 'store' || row.sellerId === 'store',
      };
    }),
    players: (live.players || []).map((player) => {
      const row = player as unknown as AuctionPlayer;
      return {
        ...row,
        avatar: row.avatar || `player${row.playerNumber}`,
        quantity: row.quantity || 1,
        resources: row.resources || {},
      };
    }),
  };
}

export default function AuctionPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);
  const game = useSelector(selectCurrentGame);
  const serverLive = game?.auction?.live;
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Auction state
  const [auctionState, setAuctionState] = useState<AuctionState>({
    currentResource: 'energy',
    currentResourceIndex: 0,
    timeRemaining: ROLE_SELECTION_TIME,
    phase: 'role-selection',
    storeInventory: getDefaultStoreInventory(),
    storePrices: pricesFromInventory(getDefaultStoreInventory()),
    trades: [],
    players: [],
  });
  
  // Fetch game state on mount
  useEffect(() => {
    const loadGameState = async () => {
      if (!gameId) {
        console.log('🎲 No gameId provided, using demo mode');
        setIsLoading(false);
        return;
      }
      
      console.log('🎲 Fetching game state for auction:', gameId);
      try {
        await dispatch(fetchGameState(gameId)).unwrap();
        console.log('✅ Game state loaded for auction');
      } catch (error) {
        console.error('❌ Failed to load game state:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadGameState();
  }, [dispatch, gameId]);
  
  const seededPlayers = useRef(false);
  useEffect(() => {
    if (!gameId || game?.phase !== 'auction') return undefined;
    const id = window.setInterval(() => {
      dispatch(fetchGameState(gameId));
    }, 1000);
    return () => window.clearInterval(id);
  }, [dispatch, game?.phase, gameId]);

  useEffect(() => {
    if (!serverLive) return;
    setAuctionState(fromSharedAuction(serverLive));
  }, [serverLive]);

  // Initialize auction players once. Later items keep the money and stock from earlier trades.
  useEffect(() => {
    if (serverLive) return;
    if (seededPlayers.current) return;
    const resourceConfig = AUCTION_RESOURCES.find(r => r.id === 'energy');
    if (!resourceConfig) return;
    if (gameId && !game?.players) return;
    
    // Use game players if available, otherwise create demo players
    if (game?.players && user) {
      seededPlayers.current = true;
      const auctionPlayers: AuctionPlayer[] = game.players.map(player => {
        const playerResources = player.resources || {};
        
        // Start in the middle of the price range (undecided)
        const middlePrice = (resourceConfig.maxPrice + resourceConfig.minPrice) / 2;
        
        return {
          playerId: player.playerId || player.userId,
          username: player.playerName || player.username || `Player ${player.playerNumber}`,
          avatar: player.avatar || `player${player.playerNumber}`,
          playerNumber: player.playerNumber,
          color: player.color || 'red',
          role: 'undecided',
          currentPrice: middlePrice,
          targetPrice: middlePrice,
          isWalking: false,
          quantity: 1,
          isAI: player.isAI || false,
          resources: playerResources as Resources,
          money: player.money || 0,
          producing: producingFor(game.map?.plots, player.playerId || player.userId),
        };
      });
      
      setAuctionState(prev => ({
        ...prev,
        players: auctionPlayers,
      }));
    } else if (!gameId) {
      seededPlayers.current = true;
      const middlePrice = (resourceConfig.maxPrice + resourceConfig.minPrice) / 2;
      const demoPlayers: AuctionPlayer[] = [
        {
          playerId: user?.userId || 'demo-player-1',
          username: user?.email?.split('@')[0] || 'You',
          avatar: 'player1',
          playerNumber: 1,
          color: 'red',
          role: 'undecided',
          currentPrice: middlePrice,
          targetPrice: middlePrice,
          isWalking: false,
          quantity: 1,
          isAI: false,
          resources: { energy: 8, minerals: 5, food: 3, oxygen: 10, water: 6, helium3: 2 } as Resources,
          money: 500,
        },
        {
          playerId: 'demo-ai-1',
          username: 'NEXUS-7',
          avatar: 'player2',
          playerNumber: 2,
          color: 'blue',
          role: 'undecided',
          currentPrice: middlePrice,
          targetPrice: middlePrice,
          isWalking: false,
          quantity: 2,
          isAI: true,
          resources: { energy: 15, minerals: 8, food: 6, oxygen: 5, water: 4, helium3: 1 } as Resources,
          money: 350,
        },
        {
          playerId: 'demo-ai-2',
          username: 'ATLAS-3',
          avatar: 'player3',
          playerNumber: 3,
          color: 'green',
          role: 'undecided',
          currentPrice: middlePrice,
          targetPrice: middlePrice,
          isWalking: false,
          quantity: 1,
          isAI: true,
          resources: { energy: 2, minerals: 12, food: 8, oxygen: 15, water: 10, helium3: 0 } as Resources,
          money: 600,
        },
        {
          playerId: 'demo-ai-3',
          username: 'VEGA-9',
          avatar: 'player4',
          playerNumber: 4,
          color: 'yellow',
          role: 'undecided',
          currentPrice: middlePrice,
          targetPrice: middlePrice,
          isWalking: false,
          quantity: 3,
          isAI: true,
          resources: { energy: 4, minerals: 3, food: 2, oxygen: 8, water: 12, helium3: 5 } as Resources,
          money: 450,
        },
      ];
      
      setAuctionState(prev => ({
        ...prev,
        players: demoPlayers,
      }));
    }
  }, [game, user, gameId]);

  useEffect(() => {
    ensureMusic();
    return () => stopMusic();
  }, []);

  useEffect(() => {
    playCountdown(Math.ceil(auctionState.timeRemaining));
  }, [auctionState.timeRemaining, auctionState.phase]);

  const heardTrades = useRef(0);
  useEffect(() => {
    if (auctionState.trades.length > heardTrades.current) playSound('trade');
    heardTrades.current = auctionState.trades.length;
  }, [auctionState.trades.length]);

  const auctionFinished = useRef(false);
  useEffect(() => {
    if (serverLive) return;
    if (auctionState.phase !== 'completed' || !gameId || !game || auctionFinished.current) return;
    auctionFinished.current = true;
    const trades = auctionState.trades
      .filter((trade) => trade.buyerId && trade.sellerId && trade.quantity > 0)
      .map((trade) => ({
        resource: trade.resource,
        buyerId: trade.buyerId,
        sellerId: trade.sellerId,
        price: trade.price,
        quantity: trade.quantity,
      }));
    apiClient.finishAuction(gameId, trades, game.etag)
      .then((result) => {
        const next = result.gameState;
        if (next) dispatch(updateGameState(next));
        if (next?.phase && next.phase !== 'auction') {
          navigate(`/game/${gameId}`);
          return;
        }
        auctionFinished.current = false;
        console.error('Auction finished without leaving trading', result);
      })
      .catch((error) => {
        auctionFinished.current = false;
        console.error('Failed to finish auction', error);
      });
  }, [auctionState.phase, auctionState.trades, gameId, game, dispatch, navigate, serverLive]);

  useEffect(() => {
    if (!gameId || !game?.phase || game.phase === 'auction') return;
    navigate(`/game/${gameId}`);
  }, [game?.phase, gameId, navigate]);
  
  // Timer countdown and phase transitions
  useEffect(() => {
    if (auctionState.phase === 'completed') return;
    
    const timer = setInterval(() => {
      if (serverLive) return;
      setAuctionState(prev => {
        if (prev.phase === 'trading' && nothingLeftToSell(prev)) {
          return advancePastTrading(prev);
        }
        if (prev.timeRemaining <= 0) {
          // Phase transition
          if (prev.phase === 'role-selection') {
            // Move to trading phase
            // AI players choose their roles if still undecided
            const quote = lockedQuote(prev.storePrices, prev.currentResource);
            const stock = prev.storeInventory[prev.currentResource] || 0;
            const updatedPlayers = prev.players.map(player => {
              if (player.isAI && player.role === 'undecided') {
                const choice = chooseRole(player, prev.currentResource, quote, stock);
                return { ...player, role: choice.role, currentPrice: choice.price, targetPrice: choice.price };
              }
              if (!player.isAI && player.role === 'undecided') {
                return {
                  ...player,
                  role: 'buyer' as const,
                  currentPrice: quote.buy,
                  targetPrice: quote.buy,
                  isWalking: false,
                };
              }
              if (player.role === 'seller' && player.currentPrice <= quote.sell) {
                const open = openSeller(quote.sell, prev.currentResource);
                return { ...player, currentPrice: open, targetPrice: open, isWalking: false };
              }
              return player;
            });
            
            const trading = {
              ...prev,
              phase: 'trading' as const,
              timeRemaining: TRADING_TIME,
              players: updatedPlayers,
            };
            return nothingLeftToSell(trading) ? advancePastTrading(trading) : trading;
          } else if (prev.phase === 'trading') {
            return advancePastTrading(prev);
          }
        }
        return { ...prev, timeRemaining: prev.timeRemaining - 1 };
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [auctionState.phase, serverLive]);
  
  // AI Trading Simulation - AI players actively negotiate during trading phase
  useEffect(() => {
    if (serverLive || auctionState.phase !== 'trading') return;
    
    const resourceConfig = AUCTION_RESOURCES.find(r => r.id === auctionState.currentResource);
    if (!resourceConfig) return;
    
    // AI decision interval - every 1.5-3 seconds, AI players adjust their prices
    const aiInterval = setInterval(() => {
      setAuctionState(prev => {
        const updatedPlayers = prev.players.map(player => {
          if (!player.isAI) return player;
          if (player.role === 'undecided') return player;
          
          // Find potential trading partners (opposite role)
          const potentialPartners = prev.players.filter(p => 
            p.playerId !== player.playerId && 
            p.role !== 'undecided' &&
            p.role !== player.role
          );
          
          const quote = lockedQuote(prev.storePrices, prev.currentResource);
          const here = player.targetPrice;
          const held = player.resources?.[prev.currentResource as keyof Resources] || 0;
          const produces = (player.producing || []).includes(prev.currentResource);
          const storeStock = prev.storeInventory[prev.currentResource] || 0;
          const moveAmount = Math.random() * 3 + 2;
          const inBand = (price: number) => price >= quote.buy && price <= quote.sell;
          const cornering = player.role === 'buyer'
            && produces
            && player.playerNumber % 2 === 0
            && storeStock > 0
            && player.money >= quote.sell;

          if (cornering) {
            return { ...player, targetPrice: quote.sell, isWalking: Math.abs(here - quote.sell) > 1 };
          }
          if (player.role === 'seller' && produces && held > 0 && held <= 2) {
            return { ...player, targetPrice: chartClamp(quote.sell + 8, prev.currentResource), isWalking: true };
          }

          const marketPartners = potentialPartners.filter((partner) => inBand(partner.targetPrice));
          if (marketPartners.length === 0) {
            if (player.role === 'seller' && held > 0) {
              return { ...player, targetPrice: Math.max(quote.buy, here - moveAmount), isWalking: true };
            }
            if (player.role === 'buyer' && storeStock > 0 && player.money >= quote.sell) {
              return { ...player, targetPrice: Math.min(quote.sell, here + moveAmount), isWalking: true };
            }
            const abstain = player.role === 'seller'
              ? chartClamp(quote.sell + 8, prev.currentResource)
              : chartClamp(quote.buy - 8, prev.currentResource);
            return { ...player, targetPrice: abstain, isWalking: true };
          }

          const closestPartner = marketPartners.reduce((closest, partner) => {
            return Math.abs(here - partner.targetPrice) < Math.abs(here - closest.targetPrice) ? partner : closest;
          });
          const partnerStock = closestPartner.resources?.[prev.currentResource as keyof Resources] || 0;
          const partnerCanTrade = player.role === 'buyer' ? partnerStock > 0 : held > 0;
          const priceDiff = Math.abs(here - closestPartner.targetPrice);
          if (priceDiff < 2 && partnerCanTrade && inBand(here)) return player;

          let newTargetPrice = player.role === 'seller' ? here - moveAmount : here + moveAmount;
          if (priceDiff < 12 && partnerCanTrade) {
            newTargetPrice = (here + closestPartner.targetPrice) / 2;
          }
          newTargetPrice = bandLimit(player.role, newTargetPrice, quote, prev.currentResource);
          
          return {
            ...player,
            targetPrice: newTargetPrice,
            isWalking: true,
          };
        });
        
        return { ...prev, players: updatedPlayers };
      });
    }, 1500 + Math.random() * 1500); // Random interval between 1.5-3 seconds
    
    return () => clearInterval(aiInterval);
  }, [auctionState.phase, auctionState.currentResource, serverLive]);
  
  // AI Role Selection during role-selection phase
  useEffect(() => {
    if (serverLive || auctionState.phase !== 'role-selection') return;
    
    const resourceConfig = AUCTION_RESOURCES.find(r => r.id === auctionState.currentResource);
    if (!resourceConfig) return;
    
    // AI players decide their roles after a short delay
    const aiRoleTimeout = setTimeout(() => {
      setAuctionState(prev => {
        const updatedPlayers = prev.players.map(player => {
          if (!player.isAI || player.role !== 'undecided') return player;
          
          const quote = lockedQuote(prev.storePrices, prev.currentResource);
          const choice = chooseRole(player, prev.currentResource, quote, prev.storeInventory[prev.currentResource] || 0);
          return {
            ...player,
            role: choice.role,
            targetPrice: choice.price,
            currentPrice: choice.price,
            isWalking: false,
          };
        });
        
        return { ...prev, players: updatedPlayers };
      });
    }, 1500 + Math.random() * 3000); // AI decides after 1.5-4.5 seconds
    
    return () => clearTimeout(aiRoleTimeout);
  }, [auctionState.phase, auctionState.currentResource, serverLive]);

  useEffect(() => {
    document.querySelector('.resource-tab.active')?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [auctionState.currentResource]);

  // Handle click in arena to set role and position
  const handleArenaClick = useCallback((price: number, isTopHalf: boolean) => {
    if (!user?.userId) return;
    if (serverLive && gameId) {
      const bid = serverLive.phase === 'role-selection'
        ? { role: (isTopHalf ? 'seller' : 'buyer') as 'seller' | 'buyer' }
        : { targetPrice: price };
      apiClient.auctionBid(gameId, bid)
        .then((result) => {
          if (result.gameState) dispatch(updateGameState(result.gameState));
        })
        .catch((error) => console.error('Auction bid failed', error));
      return;
    }
    
    const resourceConfig = AUCTION_RESOURCES.find(r => r.id === auctionState.currentResource);
    if (!resourceConfig) return;
    
    // During role-selection phase, clicking top = seller, bottom = buyer
    // During trading phase, clicking sets target price
    setAuctionState(prev => ({
      ...prev,
      players: prev.players.map(p => {
        if (p.playerId === user.userId) {
          const quote = lockedQuote(prev.storePrices, prev.currentResource);
          if (prev.phase === 'role-selection') {
            const newRole = isTopHalf ? 'seller' : 'buyer';
            const start = newRole === 'seller'
              ? openSeller(quote.sell, prev.currentResource)
              : quote.buy;
            return {
              ...p,
              role: newRole,
              currentPrice: start,
              targetPrice: start,
              isWalking: false,
            };
          }
          return {
            ...p,
            targetPrice: bandLimit(p.role, price, quote, prev.currentResource),
            isWalking: true,
          };
        }
        return p;
      }),
    }));
  }, [user?.userId, auctionState.currentResource, serverLive, gameId, dispatch]);
  
  // Handle trade execution
  const handleTradeExecuted = useCallback((trade: AuctionTrade) => {
    setAuctionState(prev => {
      const key = trade.resource as keyof Resources;
      let inventory = prev.storeInventory;
      if (trade.sellerId === 'store') {
        inventory = {
          ...inventory,
          [trade.resource]: Math.max(0, (inventory[trade.resource] || 0) - trade.quantity),
        };
      } else if (trade.buyerId === 'store') {
        inventory = {
          ...inventory,
          [trade.resource]: (inventory[trade.resource] || 0) + trade.quantity,
        };
      }
      const players = prev.players.map(p => {
        if (p.playerId === trade.buyerId) {
          return {
            ...p,
            resources: { ...p.resources, [key]: (p.resources[key] || 0) + trade.quantity },
            money: p.money - trade.price * trade.quantity,
          };
        }
        if (p.playerId === trade.sellerId) {
          return {
            ...p,
            resources: { ...p.resources, [key]: Math.max(0, (p.resources[key] || 0) - trade.quantity) },
            money: p.money + trade.price * trade.quantity,
          };
        }
        return p;
      });
      const last = prev.trades[prev.trades.length - 1];
      const samePrice = last
        && last.buyerId === trade.buyerId
        && last.sellerId === trade.sellerId
        && last.resource === trade.resource
        && last.price === trade.price
        && trade.quantity === 1;
      const trades = samePrice
        ? [...prev.trades.slice(0, -1), { ...last, quantity: last.quantity + 1, timestamp: trade.timestamp }]
        : [...prev.trades, trade];
      const next = {
        ...prev,
        storeInventory: inventory,
        trades,
        players,
      };
      if (prev.phase === 'trading' && prev.currentResource === trade.resource && nothingLeftToSell(next)) {
        return advancePastTrading(next);
      }
      return next;
    });
  }, []);
  
  // Get current player data
  const currentPlayer = useMemo(() => {
    const playerId = user?.userId || 'demo-player-1';
    return auctionState.players.find(p => p.playerId === playerId) || auctionState.players[0];
  }, [auctionState.players, user?.userId]);
  
  // Get current resource config
  const currentResourceConfig = useMemo(() => {
    return AUCTION_RESOURCES.find(r => r.id === auctionState.currentResource);
  }, [auctionState.currentResource]);
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="auction-page">
        <QuitGameButton gameId={gameId || ''} game={game} />
        <div className="auction-loading">
          <div className="loading-spinner">⏳</div>
          <p>Loading auction...</p>
        </div>
      </div>
    );
  }
  
  // Get phase display info
  const getPhaseInfo = () => {
    const name = (currentResourceConfig?.name || 'Resource').toUpperCase();
    const place = `Good ${auctionState.currentResourceIndex + 1} of ${AUCTION_RESOURCES.length}`;
    if (auctionState.phase === 'role-selection') {
      return {
        title: `NOW: ${name}`,
        subtitle: `${place}. Click the top to sell or the bottom to buy.`,
        timerColor: '#FFD700',
      };
    } else if (auctionState.phase === 'trading') {
      return {
        title: `NOW TRADING ${name}`,
        subtitle: `${place}. Buyers stop at the store sell line. Sellers stop at the buy line.`,
        timerColor: '#00FFFF',
      };
    } else {
      return {
        title: 'AUCTION COMPLETE',
        subtitle: 'All six goods have been traded',
        timerColor: '#888',
      };
    }
  };
  
  const phaseInfo = getPhaseInfo();

  return (
    <div className="auction-page">
      <QuitGameButton gameId={gameId || ''} game={game} />
      {game && gameId ? <StoreLandOffer game={game} gameId={gameId} /> : null}
      {/* Header */}
      <header className="auction-header">
        <div className="auction-title-section">
          <h1>RESOURCE AUCTION</h1>
          <div className="auction-phase-info">
            <span className="phase-title">{phaseInfo.title}</span>
            <span className="phase-subtitle">{phaseInfo.subtitle}</span>
          </div>
        </div>
        <div className="auction-timer" style={{ borderColor: phaseInfo.timerColor }}>
          <span className="timer-icon">⏱️</span>
          <span className="timer-value" style={{ color: phaseInfo.timerColor }}>
            {auctionState.timeRemaining}s
          </span>
        </div>
        <button 
          className="auction-skip-button"
          onClick={() => navigate(`/game/${gameId}`)}
        >
          Skip Auction
        </button>
        <MuteButton className="auction-mute-button" />
      </header>
      
      {/* Resource Tabs */}
      <nav className="auction-resource-tabs">
        {AUCTION_RESOURCES.map((resource, index) => {
          const playerAmount = currentPlayer?.resources?.[resource.id as keyof Resources] || 0;
          const isActive = auctionState.currentResource === resource.id;
          const isCompleted = index < auctionState.currentResourceIndex;
          const isUpcoming = index > auctionState.currentResourceIndex;
          
          return (
            <button
              key={resource.id}
              className={`resource-tab ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isUpcoming ? 'upcoming' : ''}`}
              disabled={!isActive}
              aria-current={isActive ? 'true' : undefined}
            >
              {isActive && <span className="resource-now">Now</span>}
              <span className="resource-emoji">{resource.emoji}</span>
              <span className="resource-name">{resource.name}</span>
              <span className="resource-amount">{playerAmount}</span>
              {isCompleted && <span className="completed-check">✓</span>}
            </button>
          );
        })}
      </nav>
      
      {/* Main Content */}
      <main className="auction-main">
        {/* Left Panel - Player Status */}
        <aside className="auction-sidebar auction-sidebar-left">
          <AuctionPlayerPanel
            players={auctionState.players}
            currentPlayerId={user?.userId || 'demo-player-1'}
            currentResource={auctionState.currentResource}
          />
        </aside>
        
        {/* Center - Auction Arena */}
        <section className="auction-arena-container">
          {currentResourceConfig && auctionState.phase !== 'completed' && (
            <div className={`auction-now auction-now-${auctionState.phase}`} aria-live="polite">
              <span className="auction-now-kicker">
                {auctionState.phase === 'role-selection' ? 'Choosing roles' : 'Now trading'}
              </span>
              <span className="auction-now-name">
                <span className="auction-now-emoji">{currentResourceConfig.emoji}</span>
                {currentResourceConfig.name}
              </span>
              <span className="auction-now-count">
                Good {auctionState.currentResourceIndex + 1} of {AUCTION_RESOURCES.length}
              </span>
            </div>
          )}
          {currentResourceConfig && (
            <AuctionArena
              resource={auctionState.currentResource}
              resourceConfig={currentResourceConfig}
              players={auctionState.players}
              storeInventory={auctionState.storeInventory}
              storePrices={auctionState.storePrices}
              currentPlayerId={user?.userId || 'demo-player-1'}
              phase={auctionState.phase}
              timeRemaining={auctionState.timeRemaining}
              serverDriven={Boolean(serverLive)}
              onArenaClick={handleArenaClick}
              onTradeExecuted={handleTradeExecuted}
            />
          )}
          
          {/* Role Selection Hint */}
          {auctionState.phase === 'role-selection' && (
            <div className="role-selection-hint">
              <div className="hint-seller">
                <span className="hint-arrow">↑</span>
                <span className="hint-text">Click here to SELL</span>
              </div>
              <div className="hint-divider">— OR —</div>
              <div className="hint-buyer">
                <span className="hint-text">Click here to BUY</span>
                <span className="hint-arrow">↓</span>
              </div>
            </div>
          )}
          
          {/* Player Info Bar */}
          <div className="auction-player-bar">
            <div className="player-role-display">
              <span className="role-label">Your Role:</span>
              <span className={`role-value role-${currentPlayer?.role || 'undecided'}`}>
                {currentPlayer?.role === 'seller' ? '📤 SELLER' : 
                 currentPlayer?.role === 'buyer' ? '📥 BUYER' : 
                 '❓ UNDECIDED'}
              </span>
            </div>
            <div className="player-stats">
              <span className="player-money">💰 ${currentPlayer?.money || 0}</span>
              <span className="player-resource">
                {currentResourceConfig?.emoji} {currentPlayer?.resources?.[auctionState.currentResource as keyof Resources] || 0}
              </span>
            </div>
          </div>
        </section>
        
        {/* Right Panel - Trade History */}
        <aside className="auction-sidebar auction-sidebar-right">
          <AuctionTradeHistory
            trades={auctionState.trades}
            currentResource={auctionState.currentResource}
          />
        </aside>
      </main>
    </div>
  );
}
